import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, unref as _unref } from "vue"

const _hoisted_1 = { class: "page" }
const _hoisted_2 = {
  key: 3,
  class: "section content-container"
}
const _hoisted_3 = { class: "container is-fluid" }
const _hoisted_4 = { class: "columns" }
const _hoisted_5 = { class: "column" }
const _hoisted_6 = { class: "columns triage-contacts" }
const _hoisted_7 = { class: "column" }
const _hoisted_8 = { class: "contact-information-container" }
const _hoisted_9 = { class: "contact-information" }
const _hoisted_10 = { class: "contact-information" }
const _hoisted_11 = { class: "contact-information" }
const _hoisted_12 = {
  key: 0,
  class: "contact-information"
}
const _hoisted_13 = { class: "save-bot column" }

import { ApiError } from '@/shared/services/baseApiClient';
import Toast from '@/shared/services/toast';
import TitledSection from '@/shared/components/TitledSection.vue';
import Banner from '@/shared/components/Banner.vue';
import { botsApi } from '../services/bots';
import { botAppSerivce } from '../services/botAppService';
import {
  BotActionAppViewModel,
  BotConfigurationViewModel,
  FollowUpActionType,
  FollowUpActionViewModel,
} from '@/features/bots/models';
import { HANDLE_SERIOUS_ERROR } from '@/store/app/actionTypes';
import TagsInput from '@/shared/components/TagsInput.vue';
import TriageIcon from '@/shared/components/TriageIcon.vue';
import { ConfiguredAppIntegration } from '../../apps/shared/models/configuredAppIntegration';
import { RuleViewModel } from '../models/ruleViewModel';
import RulesList from '@/features/bots/components/rules/RulesList.vue';
import { ConditionDataType } from '@/shared/components/conditions/models/conditions';
import { useStore } from 'vuex';
import { computed, onBeforeMount, ref } from 'vue';
import { useRuleConditions } from '@/features/bots/components/rules/useRuleConditions';


export default /*@__PURE__*/_defineComponent({
  __name: 'Tasks',
  setup(__props) {

const store = useStore();
const currentWorkspace = computed(() => store.getters.currentWorkspace);
const handleSeriousError = (error: string) => store.dispatch(HANDLE_SERIOUS_ERROR, error);

const botAppLoaded = ref(false);
const botAppInstallError = ref(false);
const updateInProgress = ref(false);
const reinstallInProgress = ref(false);
const enableInProgress = ref(false);

const rulesList = ref<typeof RulesList | null>(null);

const viewModel = ref<BotConfigurationViewModel>({
  disabled: false,
  captureName: true,
  captureEmail: true,
  captureEnquiry: false,
  enquiryOptions: [],
  rules: [],
  followUpActions: [],
});

const ruleConditionsManager = useRuleConditions([
  {
    id: 'flow.route',
    title: 'Enquiry Topic',
    type: ConditionDataType.Text,
    icon: 'QuestionIcon',
  },
  {
    id: 'appUser.currentUrl',
    title: 'Current Page URL',
    type: ConditionDataType.Text,
    icon: 'PageIcon',
  },
]);

onBeforeMount(() => {
  if (!currentWorkspace.value) {
    handleSeriousError('No workspace selected');
    return;
  }

  botsApi
    .get(currentWorkspace.value.id)
    .then((botResponse: BotConfigurationViewModel) => {
      initBotApp(botResponse);
      botAppLoaded.value = true;
    })
    .catch((error: ApiError) => {
      if (error.status === 404) {
        botAppInstallError.value = true;
        botAppLoaded.value = true;
        return;
      }
      handleSeriousError(error.message);
    });
});

function configRequest(
  workspaceId: string,
  installedAppId: string,
  values: any,
  previousValues: any,
  componentId: any,
) {
  return botsApi.botAppConfig(workspaceId, installedAppId, values, previousValues, componentId);
}

function getSelectedFollowUpApps(): Array<ConfiguredAppIntegration | undefined> {
  return viewModel.value.followUpActions
    .filter((action: FollowUpActionViewModel) => action.type === FollowUpActionType.AppIntegration)
    .filter((action: FollowUpActionViewModel) => action.app !== undefined)
    .map((action: FollowUpActionViewModel) => action.app);
}

function rulesUpdated(rules: RuleViewModel[]) {
  viewModel.value.rules = rules;
}

function selectedFollowUpActionsChanged(selectedApps: BotActionAppViewModel[]) {
  const nonAppActions = viewModel.value.followUpActions.filter(
    (x: FollowUpActionViewModel) => x.type !== FollowUpActionType.AppIntegration,
  );
  const appActions = selectedApps.map<FollowUpActionViewModel>((app) => ({
    type: FollowUpActionType.AppIntegration,
    app,
  }));
  viewModel.value.followUpActions = [...appActions, ...nonAppActions];
}

function reinstall() {
  if (reinstallInProgress.value) {
    return;
  }

  reinstallInProgress.value = true;

  botAppSerivce
    .fullInstall(currentWorkspace.value.id)
    .then((res) => {
      initBotApp(res);
      Toast.showSuccess('Bots have been successfully configured');
      botAppInstallError.value = false;
    })
    .catch((error) => {
      Toast.showError(error.message);
    })
    .finally(() => {
      reinstallInProgress.value = false;
    });
}

function save() {
  if (updateInProgress.value || !botAppLoaded.value || botAppInstallError.value) {
    return;
  }

  if (rulesList.value?.areRulesAreValid()) {
    updateInProgress.value = true;
    botsApi
      .update(currentWorkspace.value.id, viewModel.value)
      .then(() => {
        Toast.showSuccess('Successfully updated Bot');
      })
      .catch((err) => {
        Toast.showError(err.message);
      })
      .finally(() => {
        updateInProgress.value = false;
      });
  } else {
    Toast.showError('Please fix the errors displayed before saving');
  }
}

function enableBot() {
  if (enableInProgress.value) {
    return;
  }

  enableInProgress.value = true;

  // todo: make a copy of the viewModel on init and on save
  // then use that version of the viewModel in this request
  // so that other options aren't saved by enable or disable
  viewModel.value.disabled = false;

  botAppSerivce
    .enableBot(currentWorkspace.value, viewModel.value)
    .then(() => {
      Toast.showSuccess('Bots have been enabled');
      viewModel.value.disabled = false;
    })
    .catch((err) => {
      Toast.showError(err.message);
    })
    .finally(() => {
      enableInProgress.value = false;
    });
}

function disableBot() {
  if (!botAppLoaded.value) {
    return;
  }

  if (confirm('Are you sure you want to disable bots?')) {
    // see related todo in enableBot
    viewModel.value.disabled = true;

    botAppSerivce
      .disableBot(currentWorkspace.value, viewModel.value)
      .then(() => {
        Toast.showSuccess('Bots have been disabled');
        viewModel.value.disabled = true;
      })
      .catch((err) => {
        Toast.showError(err.message);
      });
  }
}

function initBotApp(config: BotConfigurationViewModel) {
  viewModel.value = config;
}

return (_ctx: any,_cache: any) => {
  const _component_n_button = _resolveComponent("n-button")!
  const _component_n_loading_spinner = _resolveComponent("n-loading-spinner")!
  const _component_n_toggle = _resolveComponent("n-toggle")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (viewModel.value.disabled)
      ? (_openBlock(), _createBlock(Banner, { key: 0 }, {
          default: _withCtx(() => [
            _cache[6] || (_cache[6] = _createElementVNode("div", { class: "banner-text" }, "Bots are currently disabled", -1)),
            _createVNode(_component_n_button, {
              onClick: enableBot,
              variant: "primary",
              loading: enableInProgress.value
            }, {
              default: _withCtx(() => _cache[5] || (_cache[5] = [
                _createTextVNode("Enable ")
              ])),
              _: 1
            }, 8, ["loading"])
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    (botAppInstallError.value)
      ? (_openBlock(), _createBlock(Banner, {
          key: 1,
          "show-overlay": true
        }, {
          default: _withCtx(() => [
            _cache[8] || (_cache[8] = _createElementVNode("div", { class: "banner-text" }, "There's a problem with your bot config. Let's set it up again", -1)),
            _createVNode(_component_n_button, {
              onClick: reinstall,
              variant: "primary",
              loading: reinstallInProgress.value
            }, {
              default: _withCtx(() => _cache[7] || (_cache[7] = [
                _createTextVNode(" Reconfigure ")
              ])),
              _: 1
            }, 8, ["loading"])
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    (!botAppLoaded.value)
      ? (_openBlock(), _createBlock(_component_n_loading_spinner, { key: 2 }))
      : _createCommentVNode("", true),
    (botAppLoaded.value)
      ? (_openBlock(), _createElementBlock("section", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              _createElementVNode("div", _hoisted_5, [
                _createVNode(TitledSection, { class: "container island is-fluid" }, {
                  icon: _withCtx(() => [
                    _createVNode(TriageIcon)
                  ]),
                  title: _withCtx(() => _cache[9] || (_cache[9] = [
                    _createTextVNode("Triage Contacts")
                  ])),
                  subtitle: _withCtx(() => _cache[10] || (_cache[10] = [
                    _createTextVNode("Decide what data to capture and actions to take.")
                  ])),
                  default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_6, [
                      _createElementVNode("div", _hoisted_7, [
                        _cache[12] || (_cache[12] = _createElementVNode("div", { class: "intro-message" }, "Attendant will triage contacts by asking for more information", -1)),
                        _createElementVNode("div", _hoisted_8, [
                          _createElementVNode("div", _hoisted_9, [
                            _createVNode(_component_n_toggle, {
                              id: "capture-name",
                              modelValue: viewModel.value.captureName,
                              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((viewModel.value.captureName) = $event)),
                              label: "Contact Name"
                            }, null, 8, ["modelValue"])
                          ]),
                          _createElementVNode("div", _hoisted_10, [
                            _createVNode(_component_n_toggle, {
                              id: "capture-email",
                              modelValue: viewModel.value.captureEmail,
                              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((viewModel.value.captureEmail) = $event)),
                              label: "Email Address"
                            }, null, 8, ["modelValue"])
                          ]),
                          _createElementVNode("div", _hoisted_11, [
                            _createVNode(_component_n_toggle, {
                              id: "capture-enquiry",
                              modelValue: viewModel.value.captureEnquiry,
                              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((viewModel.value.captureEnquiry) = $event)),
                              label: "Enquiry Topic"
                            }, null, 8, ["modelValue"])
                          ]),
                          (viewModel.value.captureEnquiry)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                                _cache[11] || (_cache[11] = _createElementVNode("span", { class: "contact-attribute-label" }, "Topics", -1)),
                                _createVNode(TagsInput, {
                                  class: "topic-tags",
                                  "element-id": "enquiry-topics",
                                  placeholder: "Enter your topic...",
                                  modelValue: viewModel.value.enquiryOptions,
                                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((viewModel.value.enquiryOptions) = $event)),
                                  disabled: !viewModel.value.captureEnquiry
                                }, null, 8, ["modelValue", "disabled"])
                              ]))
                            : _createCommentVNode("", true)
                        ]),
                        _createVNode(RulesList, {
                          ref_key: "rulesList",
                          ref: rulesList,
                          configRequest: configRequest,
                          "selected-apps": getSelectedFollowUpApps(),
                          rules: viewModel.value.rules,
                          "rule-conditions-manager": _unref(ruleConditionsManager),
                          onRulesUpdated: rulesUpdated,
                          onFollowupActionChange: selectedFollowUpActionsChanged
                        }, null, 8, ["selected-apps", "rules", "rule-conditions-manager"])
                      ])
                    ])
                  ]),
                  _: 1
                })
              ])
            ]),
            _createElementVNode("div", _hoisted_13, [
              _createVNode(_component_n_button, {
                onClick: _cache[4] || (_cache[4] = ($event: any) => (save())),
                variant: "primary",
                class: "save-button",
                loading: updateInProgress.value
              }, {
                default: _withCtx(() => _cache[13] || (_cache[13] = [
                  _createTextVNode("Save ")
                ])),
                _: 1
              }, 8, ["loading"]),
              (!viewModel.value.disabled && !botAppInstallError.value)
                ? (_openBlock(), _createElementBlock("a", {
                    key: 0,
                    onClick: disableBot
                  }, "I don't want to use bots..."))
                : _createCommentVNode("", true)
            ])
          ])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})